/**
 * Converts a language code (e.g., "en-US", "nl-NL") to its corresponding flag emoji.
 *
 * @param langCode - Language code in format "xx-YY" where YY is the ISO 3166-1 alpha-2 country code
 * @returns Flag emoji or globe emoji (🌐) if no valid country code found
 *
 * @example
 * getLanguageFlag("en-US") // returns "🇺🇸"
 * getLanguageFlag("nl-NL") // returns "🇳🇱"
 * getLanguageFlag("invalid") // returns "🌐"
 */
export function getLanguageFlag(langCode: string): string {
  if (!langCode) return '🌐'

  // Extract country code from language code (last part after hyphen)
  const parts = langCode.split('-')
  const countryCode = parts.length > 1 ? parts[1] : parts[0]

  // Validate it's a 2-letter country code
  const upperCode = countryCode.toUpperCase()
  if (!/^[A-Z]{2}$/.test(upperCode)) return '🌐'

  // Convert to flag emoji using regional indicator symbols
  // Each letter A-Z maps to Unicode codepoint 127462-127487 (🇦-🇿)
  // Formula: codepoint = 127397 + letter.charCodeAt(0)
  const codePoints = Array.from(upperCode).map((char) => 127397 + char.charCodeAt(0))

  return String.fromCodePoint(...codePoints)
}

/**
 * Gets the full language display name in the user's UI language.
 * Includes both language and region (e.g., "English (United States)" in EN, "Engels (Verenigde Staten)" in NL).
 *
 * @param langCode - Language code (e.g., "en-US", "nl-NL")
 * @param uiLocale - UI language code (e.g., "en", "nl") - defaults to "en"
 * @param fallback - Optional fallback value if DisplayNames fails
 * @returns Display name in the UI language or fallback
 *
 * @example
 * getLanguageDisplayName("en-US", "en") // returns "English (United States)"
 * getLanguageDisplayName("en-US", "nl") // returns "Engels (Verenigde Staten)"
 * getLanguageDisplayName("nl-NL", "nl") // returns "Nederlands (Nederland)"
 */
export function getLanguageDisplayName(
  langCode: string,
  uiLocale: string = 'en-US',
  fallback?: string
): string | undefined {
  try {
    const safeLocale = uiLocale?.replace('_', '-') || 'en-US'
    // Use the UI language for display names so they match the interface language
    const displayNames = new Intl.DisplayNames([safeLocale], {
      type: 'language',
      languageDisplay: 'standard',
    })

    // Get the display name for the full language code (includes region)
    const displayName = displayNames.of(langCode)

    return displayName || fallback
  } catch (error) {
    // Fallback if Intl.DisplayNames fails (e.g., unsupported locale)
    console.warn(`Failed to get display name for language code: ${langCode}`, error)
    return fallback
  }
}

/**
 * Common languages supported in the application
 */
export const SUPPORTED_LANGUAGES = [
  { id: 'nl-NL', name: 'Dutch (Netherlands)' },
  { id: 'nl-BE', name: 'Dutch (Belgium)' },
  { id: 'en-US', name: 'English (United States)' },
  { id: 'en-GB', name: 'English (United Kingdom)' },
  { id: 'en-AU', name: 'English (Australia)' },
  { id: 'en-IE', name: 'English (Ireland)' },
  { id: 'de-DE', name: 'German (Germany)' },
  { id: 'de-AT', name: 'German (Austria)' },
  { id: 'de-CH', name: 'German (Switzerland)' },
  { id: 'fr-FR', name: 'French (France)' },
  { id: 'fr-BE', name: 'French (Belgium)' },
  { id: 'es-ES', name: 'Spanish (Spain)' },
  { id: 'it-IT', name: 'Italian (Italy)' },
  { id: 'pt-PT', name: 'Portuguese (Portugal)' },
  { id: 'pl-PL', name: 'Polish (Poland)' },
  { id: 'sv-SE', name: 'Swedish (Sweden)' },
  { id: 'da-DK', name: 'Danish (Denmark)' },
  { id: 'nb-NO', name: 'Norwegian (Norway)' },
  { id: 'el-GR', name: 'Greek (Greece)' },
  { id: 'tr-TR', name: 'Turkish (Turkey)' },
  { id: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
  { id: 'zh-CN', name: 'Chinese (Simplified)' },
  { id: 'hr-HR', name: 'Croatian (Croatia)' },
]

